import React from 'react';
import { Fit, Text, fs } from '../parts/fields';
import { EmptyNote, Head, Logo } from '../parts/chrome';
import { useSlide } from '../parts/context';
import { money } from '../text';
import { ComputedPricing, PricingRow } from '../types';

const sqft = (n: number): string => n.toLocaleString('en-US', { maximumFractionDigits: n < 100 ? 1 : 0 });
const signed = (n: number, currency: string): string => (n < 0 ? `−${money(Math.abs(n), currency, true)}` : money(n, currency, true));

// #region wallLine
function wallLine(p: ComputedPricing): string {
    const w = p.wall;
    const parts = [`One ${w.width_ft} × ${w.height_ft} ft wall`, `${w.panels} panels`, `${sqft(w.sqft)} sq ft`];
    if (p.tier === 'artist' && p.royalty_percent !== null) parts.push(`${p.royalty_percent}% royalty`);
    return parts.join(' · ');
}

// #region PricingLayout
/** What a brand earns per material: per wall order, per month, and the line-by-line maths. Numbers come from the server. */
export const PricingLayout: React.FunctionComponent = (): React.ReactElement => {
    const { computed } = useSlide();
    const p = computed.pricing;
    const n = p?.rows.length ?? 0;
    return (
        <>
            <div className="wd-pad" style={{ paddingTop: 88, paddingBottom: 112 }}>
                <Head steps={[[30, 72], [60, 62], [999, 54]]} style={{ marginBottom: 24 }} titleStyle={{ maxWidth: 1400 }} />
                {p && n ? (
                    <Fit style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 32, flexWrap: 'wrap' }}>
                            <span className="wd-pill" style={{ fontSize: fs(21) }}>{p.tier === 'artist' ? 'Artist' : 'Store Owner'} pricing</span>
                            <span className="wd-muted" style={{ fontSize: fs(24) }}>{wallLine(p)}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gap: 32, maxWidth: n === 1 ? 860 : undefined }}>
                            {p.rows.map((row, i) => <MaterialCard key={row.material || i} row={row} pricing={p} dense={n >= 3} />)}
                        </div>
                        <footer style={{ marginTop: 'auto', paddingTop: 24, display: 'flex', gap: 48, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Text k="note" className="wd-lead" style={{ fontSize: fs(23), color: 'var(--wd-body)', maxWidth: 820 }} placeholder="A note under the numbers (optional)" max={200} />
                            {p.fees_note ? <p className="wd-muted" style={{ fontSize: fs(19), maxWidth: 820, textAlign: 'right', marginLeft: 'auto', lineHeight: 1.4 }}>{p.fees_note}</p> : null}
                        </footer>
                    </Fit>
                ) : (
                    <EmptyNote style={{ flex: 1 }}>The numbers for each material appear here in the preview, worked out from current Wallmates pricing.</EmptyNote>
                )}
            </div>
            <Logo />
        </>
    );
};

// #region MaterialCard
const MaterialCard: React.FunctionComponent<{ row: PricingRow; pricing: ComputedPricing; dense: boolean }> = ({ row, pricing, dense }): React.ReactElement => {
    const c = pricing.currency || 'USD';
    const artist = pricing.tier === 'artist';
    const rate = artist ? null : row.retail_sqft !== null ? `You charge ${money(row.retail_sqft, c, true)}/sq ft · cost ${money(row.cost_sqft, c, true)}` : `Cost ${money(row.cost_sqft, c, true)}/sq ft`;
    const line: React.CSSProperties = { padding: '8px 0', borderTop: '1.5px solid var(--wd-line)' };
    return (
        <article className="wd-card" style={{ display: 'flex', flexDirection: 'column', padding: dense ? '34px 38px 30px' : '40px 46px 36px', minWidth: 0 }}>
            <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <h3 className="wd-h" style={{ fontSize: fs(dense ? 32 : 36), letterSpacing: 0 }}>{row.label}</h3>
                {row.margin_pct !== null ? <span className="wd-pill" style={{ fontSize: fs(19), padding: '7px 16px', flex: 'none' }}>{Math.round(row.margin_pct)}% margin</span> : null}
            </header>
            {rate ? <p className="wd-muted" style={{ fontSize: fs(20), marginTop: 6 }}>{rate}</p> : null}
            <div style={{ marginTop: 22, display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
                <span className="wd-num" style={{ fontSize: fs(dense ? 80 : 92), lineHeight: 1, color: 'var(--wd-accent)' }}>{money(row.per_order, c)}</span>
                <span className="wd-muted" style={{ fontSize: fs(22), lineHeight: 1.2 }}>{artist ? 'you earn' : 'profit'}<br />per wall order</span>
            </div>
            <p style={{ marginTop: 14, fontSize: fs(25), color: 'var(--wd-body)' }}>
                <strong className="wd-num" style={{ letterSpacing: 0 }}>{money(row.monthly, c)}</strong> a month at {pricing.orders_per_month} {pricing.orders_per_month === 1 ? 'order' : 'orders'}
            </p>
            {row.breakdown.length ? (
                <dl style={{ marginTop: 22, display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 24, fontSize: fs(dense ? 21 : 23), lineHeight: 1.3 }}>
                    {row.breakdown.map((b, i) => (
                        <React.Fragment key={i}>
                            <dt className="wd-muted" style={line}>{b.label}</dt>
                            <dd className="wd-num" style={{ ...line, margin: 0, textAlign: 'right', color: b.amount < 0 ? 'var(--wd-muted)' : 'var(--wd-ink)', fontWeight: 500, letterSpacing: 0 }}>{signed(b.amount, c)}</dd>
                        </React.Fragment>
                    ))}
                    <dt style={{ ...line, borderTop: '2.5px solid var(--wd-ink)', fontWeight: 650, color: 'var(--wd-ink)', paddingBottom: 0 }}>{artist ? 'You earn' : 'Your profit'}</dt>
                    <dd className="wd-num" style={{ ...line, borderTop: '2.5px solid var(--wd-ink)', margin: 0, paddingBottom: 0, textAlign: 'right', color: 'var(--wd-ink)', letterSpacing: 0 }}>{money(row.per_order, c, true)}</dd>
                </dl>
            ) : null}
        </article>
    );
};
